import { isEmpty, path, pathOr } from 'ramda'

import appSelectors from 'App/duck/selectors'

import { Errors, HMPayState } from '../types'
import { FILTER_NAME } from './constants'

const getHMPayState = (state): HMPayState => state.account.hmPay
const getSelectedTab = (state): string => getHMPayState(state).selectedTab
const getIsTargetApp = (state): boolean => appSelectors.getIsTargetApp(state)

// ----------------------------------------
// LOADERS
// ----------------------------------------
const getIsLoading = (state): boolean =>
  appSelectors.getShowLoaders(state) || getHMPayState(state).isLoading
const getIsLoadMoreUsed = (state): boolean => getHMPayState(state).isLoadMoreUsed
const getLoadingOrderId = (state): string => getHMPayState(state).loadingOrderId
const getIsFailedToLoad = (state): boolean => getHMPayState(state).isFailedToLoad

// ----------------------------------------
// ORDERS
// ----------------------------------------
const getOrderById = (id, state): any =>
  pathOr(null, ['orders', 'invoices', id], getHMPayState(state))

const getOrderIdByHmOrderNumber = (hmOrderNumber, state): any =>
  (
    (Object.values(pathOr({}, ['orders', 'invoices'], getHMPayState(state))).find(
      (order: any) => order.hmOrderNumber === hmOrderNumber,
    ) || {}) as any
  ).orderId

const getOrderFilter = (filter, state): any =>
  pathOr({}, ['orders', 'filters', filter], getHMPayState(state))

const getExpandedOrderIdForFilter = (filter, state): any =>
  getOrderFilter(filter, state).expandedOrderId

const getIdsForOrderFilter = (filter, state): any => getOrderFilter(filter, state).orderIds || []

const getTotalNumberForOrderFilter = (filter, state): any =>
  getOrderFilter(filter, state).totalNumberOfOrders

const getIsLoadingForOrderFilter = (filter, state): any =>
  getOrderFilter(filter, state).isLoading || false

const getHasLoadedForOrderFilter = (filter, state): any =>
  getOrderFilter(filter, state).hasLoaded || false

const getErrorForOrderFilter = (filter, state): any => getOrderFilter(filter, state).error

const getIsKlarnaAuthenticated = (state): boolean =>
  appSelectors.getIsPostPurchaseAuthenticated(state) &&
  getHMPayState(state).error !== Errors.OAUTH_EXPIRED
const getShouldFetchOrdersForFilter = (filter, state): any =>
  !!(
    !getIsLoadingForOrderFilter(filter, state) &&
    !getHasLoadedForOrderFilter(filter, state) &&
    !getErrorForOrderFilter(filter, state) &&
    getIsKlarnaAuthenticated(state)
  )

const getShouldShowLoadMoreForOrderFilter = (filter, state): any =>
  getIdsForOrderFilter(filter, state).length < getTotalNumberForOrderFilter(filter, state)

const getIsLoadingMoreForOrderFilter = (filter, state): any =>
  getOrderFilter(filter, state).isLoadingMore

const getNextOffsetForOrderFilter = (filter, state): any =>
  getOrderFilter(filter, state).nextOffset || 0

// ----------------------------------------
// MONTHLY INVOICES
// ----------------------------------------

const getMonthlyInvoiceFilter = (filter, state): any =>
  pathOr({}, ['monthlyInvoices', 'filters', filter], getHMPayState(state))

const getMonthlyInvoicesIsLoading = (state): any =>
  path(['monthlyInvoices', 'isLoading'], getHMPayState(state))

const getMonthlyInvoicesHasLoaded = (state): any =>
  path(['monthlyInvoices', 'hasLoaded'], getHMPayState(state))

const getMonthlyInvoicesNextOffset = (state): any =>
  path(['monthlyInvoices', 'nextOffset'], getHMPayState(state)) || 0

const getMonthlyInvoicesTotalNumber = (state): any =>
  path(['monthlyInvoices', 'totalNumberOfInvoices'], getHMPayState(state))

const getMonthlyInvoicesError = (state): any =>
  path(['monthlyInvoices', 'error'], getHMPayState(state))

const getMonthlyInvoiceById = (id, state): any =>
  pathOr(null, ['monthlyInvoices', 'invoices', id], getHMPayState(state))

const getMonthlyInvoiceIdsForFilter = (filter, state): any =>
  getMonthlyInvoiceFilter(filter, state).monthlyInvoiceIds || []

const getMonthlyInvoiceExpandedForFilter = (filter, state): any =>
  getMonthlyInvoiceFilter(filter, state).expandedMonthlyInvoiceId

const getMonthlyInvoiceExpandedOrderForFilter = (filter, state): any =>
  getMonthlyInvoiceFilter(filter, state).expandedOrderId

const getMonthlyInvoicesShouldShowLoadMoreButton = (state): any =>
  getMonthlyInvoiceIdsForFilter(FILTER_NAME.ALL, state).length <
  getMonthlyInvoicesTotalNumber(state)

const getIsMonthlyInvoiceOrdersLoaded = (invoiceId, state): any =>
  getMonthlyInvoiceById(invoiceId, state).isLoaded

const getIsMonthlyInvoiceOrdersLoading = (invoiceId, state): any =>
  getMonthlyInvoiceById(invoiceId, state).isLoading || false

const getMonthlyInvoiceOrderIds = (invoiceId, state): any =>
  getMonthlyInvoiceById(invoiceId, state).orderIds || []

const getMonthlyInvoiceNextOffset = (invoiceId, state): any =>
  getMonthlyInvoiceById(invoiceId, state).nextOffset || 0

const getTotalNumberOfMonthlyInvoiceOrders = (invoiceId, state): any =>
  getMonthlyInvoiceById(invoiceId, state).totalNumberOfOrders

const getShouldShowMonthlyInvoiceOrdersLoadMoreButton = (invoiceId, state): any =>
  getMonthlyInvoiceOrderIds(invoiceId, state).length <
  getTotalNumberOfMonthlyInvoiceOrders(invoiceId, state)

// ----------------------------------------
// OTHERS
// ----------------------------------------

const getIfUserHasInvoice = (invoiceId, state): any =>
  !!getOrderById(invoiceId, state) || !!getMonthlyInvoiceById(invoiceId, state)
const getIsInvoiceInFilter = (invoiceId, filter, state): any =>
  getIdsForOrderFilter(filter, state).includes(invoiceId) ||
  getMonthlyInvoiceIdsForFilter(filter, state).includes(invoiceId)

const getIsKlarnaAuthRequested = (state): any => getHMPayState(state).isKlarnaAuthRequested
const getIsFetchingKlarnaProfile = (state): any => getHMPayState(state).isFetchingKlarnaProfile

const getProfileOperations = (state): any => getHMPayState(state).operations
const getManageMonthlyOperation = (state): any => getProfileOperations(state).manageMonthly
const getManageCardOperation = (state): any => getProfileOperations(state).manageCard
const getShouldShowManageCardButton = (state): any =>
  appSelectors.getPayLaterManageCardEnabled(state) && !!getManageCardOperation(state)
const getManageMonthlyOperationToken = (state): any =>
  path(['token'], getManageMonthlyOperation(state))
const getMonthlySubscription = (state): any => getHMPayState(state).subscription
const getMonthlySubscriptionId = (state): any =>
  path(['subscription_id'], getMonthlySubscription(state))
const getMonthlySubscriptionStatus = (state): any => path(['status'], getMonthlySubscription(state))

const getShouldFetchKlarnaProfile = (state): any =>
  getIsKlarnaAuthenticated(state) &&
  !getIsFetchingKlarnaProfile(state) &&
  isEmpty(getProfileOperations(state))

const getShouldFetchMonthlyInvoices = (state): any =>
  !!(
    getIsKlarnaAuthenticated(state) &&
    !getMonthlyInvoicesIsLoading(state) &&
    !getMonthlyInvoicesHasLoaded(state) &&
    getMonthlySubscriptionId(state)
  )

export default {
  getSelectedTab,
  getHMPayState,
  getIsLoading,
  getIsLoadingMoreForOrderFilter,
  getIsLoadMoreUsed,
  getOrderById,
  getOrderIdByHmOrderNumber,
  getOrderFilter,
  getIdsForOrderFilter,
  getTotalNumberForOrderFilter,
  getIsLoadingForOrderFilter,
  getHasLoadedForOrderFilter,
  getErrorForOrderFilter,
  getShouldFetchOrdersForFilter,
  getShouldShowLoadMoreForOrderFilter,
  getNextOffsetForOrderFilter,
  getIsKlarnaAuthenticated,
  getIsKlarnaAuthRequested,
  getLoadingOrderId,
  getIsFailedToLoad,
  getShouldFetchKlarnaProfile,
  getManageMonthlyOperation,
  getManageMonthlyOperationToken,
  getManageCardOperation,
  getMonthlySubscription,
  getMonthlySubscriptionId,
  getMonthlySubscriptionStatus,
  getMonthlyInvoicesIsLoading,
  getMonthlyInvoicesHasLoaded,
  getMonthlyInvoicesError,
  getMonthlyInvoicesNextOffset,
  getMonthlyInvoicesTotalNumber,
  getMonthlyInvoiceById,
  getMonthlyInvoiceIdsForFilter,
  getMonthlyInvoicesShouldShowLoadMoreButton,
  getShouldFetchMonthlyInvoices,
  getIsMonthlyInvoiceOrdersLoaded,
  getIsMonthlyInvoiceOrdersLoading,
  getMonthlyInvoiceOrderIds,
  getMonthlyInvoiceNextOffset,
  getTotalNumberOfMonthlyInvoiceOrders,
  getShouldShowMonthlyInvoiceOrdersLoadMoreButton,
  getIfUserHasInvoice,
  getIsInvoiceInFilter,
  getMonthlyInvoiceExpandedOrderForFilter,
  getMonthlyInvoiceExpandedForFilter,
  getExpandedOrderIdForFilter,
  getShouldShowManageCardButton,
  getIsTargetApp,
}
